import React from 'react';
import { useIntl } from 'umi';
import { useLocalStore } from 'mobx-react';
import { message } from '@/components';
import * as API from '@/api';
import { user as userModel, rtc, meeting as meetingModel } from '@/models';

export const useOpenAudio = () => {
  const {
    user,
    rtc: RTCInstance,
    meeting,
  } = useLocalStore(() => {
    return {
      user: userModel,
      rtc,
      meeting: meetingModel,
    };
  });
  const { formatMessage } = useIntl();

  const openAudio = React.useCallback(async () => {
    if (RTCInstance?.breakLineReconnecting) {
      return;
    }
    if (isPad && RTCInstance?.micIsNotAvailable) {
      message.info({
        content: formatMessage({ id: 'NO_DEVICE' }),
        icon: null,
      });
      return;
    }
    if (RTCInstance?.isOpenAudioLoading) {
      return;
    }
    RTCInstance!.isOpenAudioLoading = true;
    const isOpen = RTCInstance?.isOpenAudio
      ? API.DEVICE_STATUS_REQUEST.CLOSE
      : API.DEVICE_STATUS_REQUEST.OPEN;
    // 先 quiet 允许开启后，再上报设备状态
    try {
      await user?.quiet(String(RTCInstance?.cid), isOpen, API.SPACE_STATUS.OPEN);
      await user?.updateDeviceStatus(String(RTCInstance?.cid), API.DEVICE_TYPE.AUDIO, isOpen);
      RTCInstance?.isOpenAudio ? await RTCInstance.closeMic() : await RTCInstance?.openMic(true);
    } catch (err: any) {
      if (err.status === 5005) {
        message.info({
          content: formatMessage({ id: 'NOT_ALLOWED_TURNED_ON_DEVICE' }),
          noIcon: true,
        });
      } else {
        message.info({
          content: err.msg || err.message,
          noIcon: true,
        });
      }
    } finally {
      RTCInstance!.isOpenAudioLoading = false;
    }
  }, [RTCInstance]);

  const openAudioSpace = React.useCallback(async () => {
    if (RTCInstance?.breakLineReconnecting) {
      return;
    }
    RTCInstance!.isOpenAudioLoading = true;
    const isOpen = RTCInstance?.isOpenAudio
      ? API.DEVICE_STATUS_REQUEST.CLOSE
      : API.DEVICE_STATUS_REQUEST.OPEN;
    // 先 quiet 允许开启后，再上报设备状态
    try {
      await user?.quiet(String(RTCInstance?.cid), isOpen, API.SPACE_STATUS.SPACE_ON);
      await user?.updateDeviceStatus(String(RTCInstance?.cid), API.DEVICE_TYPE.AUDIO, isOpen);
      RTCInstance?.isOpenAudio ? await RTCInstance.closeMic() : await RTCInstance?.openMic(true);
      meeting?.setTemporaryUnmute(RTCInstance?.isOpenAudio);
      console.log('长按输出-打开了麦克风', !!isOpen);
    } catch (err: any) {
      if (err.status === 5005) {
        message.info({
          content: formatMessage({ id: 'NOT_ALLOWED_TURNED_ON_DEVICE' }),
          noIcon: true,
        });
      } else {
        message.info({
          content: err.msg || err.message,
          noIcon: true,
        });
      }
    } finally {
      RTCInstance!.isOpenAudioLoading = false;
    }
  }, [RTCInstance]);

  const closeAudioSpace = React.useCallback(async () => {
    if (RTCInstance?.breakLineReconnecting) {
      return;
    }
    RTCInstance!.isOpenAudioLoading = true;
    const isOpen = API.DEVICE_STATUS_REQUEST.CLOSE;
    // 先 quiet 允许开启后，再上报设备状态
    try {
      await user?.quiet(String(RTCInstance?.cid), isOpen, API.SPACE_STATUS.SPACE_ON);
      await user?.updateDeviceStatus(String(RTCInstance?.cid), API.DEVICE_TYPE.AUDIO, isOpen);
      // meeting?.clearSpeakerYxUid(); // 清除本地当前说话人
      await RTCInstance.closeMic();
      meeting?.setTemporaryUnmute(false);
    } catch (err: any) {
      if (err.status === 5005) {
        message.info({
          content: formatMessage({ id: 'NOT_ALLOWED_TURNED_ON_DEVICE' }),
          noIcon: true,
        });
      } else {
        message.info({
          content: err.msg || err.message,
          noIcon: true,
        });
      }
    } finally {
      RTCInstance!.isOpenAudioLoading = false;
    }
  }, [RTCInstance]);

  return { openAudio, openAudioSpace, closeAudioSpace };
};
